import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { ShoppingBag, User, Menu, X } from "lucide-react";

export default function Header() {
  const { user, logout, setLoginOpen, isAdminAuthenticated } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItem = "text-sm tracking-wide hover:opacity-60 transition-opacity";

  const openLogin = () => setLoginOpen(true);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 bg-white/75 backdrop-blur-xl border-b border-[#E5E3DE]"
    >
      <div className="px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="font-display text-xl md:text-2xl tracking-tighter">
          CROWN <span className="text-[#C9A961]">&amp;</span> STITCH
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <NavLink to="/shop" data-testid="nav-shop" className={navItem}>Shop</NavLink>
          <NavLink to="/custom" data-testid="nav-custom" className={navItem}>Custom &amp; Bulk</NavLink>
          <NavLink to="/about" data-testid="nav-about" className={navItem}>Atelier</NavLink>
        </nav>

        <div className="flex items-center gap-5 md:gap-6">
          {user ? (
            <>
              {isAdminAuthenticated && (
                <Link to="/admin" data-testid="nav-admin" className={navItem + " hidden md:inline"}>Admin</Link>
              )}
              <Link to="/account" data-testid="nav-account" className={navItem + " hidden md:flex items-center gap-2 whitespace-nowrap"}>
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                ) : (
                  <User size={16} />
                )}
                <span className="inline">{user.name.split(" ")[0]}</span>
              </Link>
              <button onClick={logout} data-testid="logout-btn" className={navItem + " hidden md:inline"}>
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={openLogin}
              data-testid="open-login-btn"
              aria-label="Sign in"
              className="hover:opacity-60 hidden md:inline"
            >
              <User size={18} strokeWidth={1.5} />
            </button>
          )}
          <button
            data-testid="cart-button"
            onClick={() => navigate("/cart")}
            aria-label="Cart"
            className="relative hover:opacity-60"
          >
            <ShoppingBag size={18} strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-2 -right-2 bg-black text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full font-bold"
              >
                {count}
              </span>
            )}
          </button>
          <button
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            className="md:hidden"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="md:hidden border-t border-[#E5E3DE] bg-white px-6 py-6 flex flex-col gap-5">
          <Link to="/shop" onClick={() => setMobileOpen(false)} className="text-base">Shop</Link>
          <Link to="/custom" onClick={() => setMobileOpen(false)} className="text-base">Custom &amp; Bulk</Link>
          <Link to="/about" onClick={() => setMobileOpen(false)} className="text-base">Atelier</Link>
          {isAdminAuthenticated && (
            <Link to="/admin" onClick={() => setMobileOpen(false)} className="text-base">Admin</Link>
          )}
          {user ? (
            <>
              <Link to="/account" onClick={() => setMobileOpen(false)} className="text-base flex items-center gap-2">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <User size={16} />
                )}
                <span>{user.name?.split(" ")?.[0] || "Account"}</span>
              </Link>
              <button onClick={() => { setMobileOpen(false); logout(); }} className="text-base text-left">Logout</button>
            </>
          ) : (
            <button onClick={() => { setMobileOpen(false); openLogin(); }} className="text-base text-left">
              Continue with Google
            </button>
          )}
        </div>
      )}
    </header>
  );
}
